import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import type { CapstoneApproach, CapstoneForLearner, CapstoneHint } from '@lp/shared';
import {
  ArrowLeft,
  CheckCircle2,
  Circle,
  Eye,
  Lightbulb,
  Lock,
  MessageSquare,
  Package,
  Trophy,
  X,
} from 'lucide-react';
import { ApiError } from '../lib/api.js';
import { learnerKeys, useCapstone } from '../lib/useLearnerData.js';
import { useChecklistToggle } from '../lib/useChecklist.js';
import { useReveal } from '../lib/useReveal.js';
import { useScrollSpy } from '../lib/useScrollSpy.js';
import { useMediaQuery, WIDE_SCREEN_QUERY } from '../lib/useMediaQuery.js';
import { PageContainer } from '../components/layout/AppShell.js';
import { Markdown } from '../components/Markdown.js';
import { ConfirmDialog } from '../components/Modal.js';
import {
  Alert,
  Badge,
  Button,
  Card,
  Disclosure,
  DotProgress,
  ProgressBar,
  clsx,
} from '../components/ui.js';
import { ChatPanel } from '../components/learner/ChatPanel.js';
import { ResourceRow } from '../components/learner/AssignmentSection.js';
import { ApiErrorView, CapstoneSkeleton } from '../components/learner/StateViews.js';
import { NotFoundPage } from './NotFoundPage.js';

/**
 * The capstone brief.
 *
 * Progressive disclosure is the whole interaction. Nothing gated is open when
 * the page loads, opening anything is one deliberate click, and the reference
 * solution needs a typed phrase — because a brief whose answer is one stray
 * click away is not a brief. Reveal state lives on the server, so what the
 * learner has already opened stays open across devices and reloads.
 */
export function CapstonePage() {
  const { courseSlug = '' } = useParams<{ courseSlug: string }>();
  const queryKey = learnerKeys.capstone(courseSlug);
  const { data, isPending, error, refetch } = useCapstone(courseSlug);
  const wide = useMediaQuery(WIDE_SCREEN_QUERY);
  const [chatOpen, setChatOpen] = useState(false);

  const sections = useMemo(() => buildSections(data), [data]);
  const activeId = useScrollSpy(sections.map((section) => section.id));

  if (error instanceof ApiError && error.isNotFound) {
    return (
      <NotFoundPage
        title="There is no capstone here"
        description="This course either has no capstone project, or it has not been released yet."
      />
    );
  }

  if (isPending) {
    return (
      <PageContainer wide>
        <CapstoneSkeleton />
      </PageContainer>
    );
  }

  if (error || !data) {
    return (
      <PageContainer wide>
        <ApiErrorView error={error} retry={() => void refetch()} context="this capstone" />
      </PageContainer>
    );
  }

  const milestonesDone = data.milestones.filter((milestone) => milestone.done).length;

  return (
    <PageContainer wide>
      {data.isPreview ? (
        <Alert
          tone="warn"
          title="Unpublished draft — nobody else can see this"
          className="mb-5"
        >
          You are looking at the learner view of a capstone that has not been published. No learner
          on this course can reach this page yet.
        </Alert>
      ) : null}

      <Hero capstone={data} milestonesDone={milestonesDone} />

      <div className="grid gap-8 mt-8 lg:grid-cols-[15rem_minmax(0,1fr)] items-start">
        <Sidebar
          sections={sections}
          activeId={activeId}
          wide={wide}
          hasStarters={data.resources.some((resource) => resource.isStarter)}
          canAskAi={data.chatEnabled}
          onAskAi={() => setChatOpen(true)}
        />

        <div className="min-w-0 flex flex-col gap-10">
          <Section id="overview" title="Overview">
            <Markdown>{data.overviewMd}</Markdown>
          </Section>

          <Section id="problem" title="The problem">
            <Markdown>{data.problemStatementMd}</Markdown>
          </Section>

          {data.locked ? (
            <Alert tone="warn" title="The rest of the brief is still locked" icon={<Lock size={16} />}>
              <p>{data.lockedReason ?? 'This course unlocks in order.'}</p>
              <p className="mt-2 text-ink-muted">
                The overview and the problem statement above are yours to read now, so you know
                what you are working towards. The specification, the milestones, the hints, the
                worked approaches and the reference solution all appear here as soon as the
                sessions ahead of you are complete.
              </p>
              <p className="mt-3">
                <Link
                  to={`/courses/${data.courseSlug}`}
                  className="text-brand-text underline underline-offset-2"
                >
                  Back to the course track
                </Link>
              </p>
            </Alert>
          ) : (
            <>
              {data.specSections.length > 0 ? (
                <Section id="specification" title="What it has to do">
                  <p className="text-sm text-ink-muted mb-4 max-w-prose leading-relaxed">
                    Each requirement below has its own address — use the link beside a heading to
                    send someone straight to that one.
                  </p>
                  <div className="flex flex-col gap-6">
                    {[...data.specSections]
                      .sort((a, b) => a.orderIndex - b.orderIndex)
                      .map((spec) => (
                        <article
                          key={spec.id}
                          id={spec.anchor}
                          className="border-l-2 border-line-strong pl-4"
                        >
                          <h3 className="font-semibold text-ink group flex items-baseline gap-2">
                            {spec.title}
                            <a
                              href={`#${spec.anchor}`}
                              className="text-xs font-normal text-ink-subtle hover:text-brand-text"
                              aria-label={`Link to “${spec.title}”`}
                            >
                              #
                            </a>
                          </h3>
                          <div className="mt-2">
                            <Markdown>{spec.bodyMd}</Markdown>
                          </div>
                        </article>
                      ))}
                  </div>
                </Section>
              ) : null}

              {data.milestones.length > 0 ? (
                <Milestones capstone={data} queryKey={queryKey} done={milestonesDone} />
              ) : null}

              {data.hints.length > 0 ? (
                <Section id="hints" title="Hints">
                  <p className="text-sm text-ink-muted mb-4 max-w-prose leading-relaxed">
                    Each hint stays closed until you open it. Getting stuck for a while is part of
                    the exercise — but being stuck for a day is not, so they are here when you want
                    them.
                  </p>
                  <div className="flex flex-col gap-2">
                    {[...data.hints]
                      .sort((a, b) => a.orderIndex - b.orderIndex)
                      .map((hint) => (
                        <HintBlock key={hint.id} hint={hint} queryKey={queryKey} />
                      ))}
                  </div>
                </Section>
              ) : null}

              {data.approaches.length > 0 ? (
                <Section id="approaches" title="Recommended approaches">
                  <div className="flex flex-col gap-3">
                    {[...data.approaches]
                      .sort((a, b) => a.orderIndex - b.orderIndex)
                      .map((approach) => (
                        <ApproachBlock
                          key={approach.id}
                          approach={approach}
                          queryKey={queryKey}
                        />
                      ))}
                  </div>
                </Section>
              ) : null}

              {data.resources.length > 0 ? (
                <Section id="resources" title="Starter resources">
                  <ul className="flex flex-col gap-2">
                    {data.resources.map((resource) => (
                      <li key={resource.id}>
                        <ResourceRow resource={resource} />
                      </li>
                    ))}
                  </ul>
                </Section>
              ) : null}

              {data.selfChecks.length > 0 ? (
                <SelfChecks capstone={data} queryKey={queryKey} />
              ) : null}

              {data.hasReferenceSolution ? (
                <ReferenceSolution capstone={data} queryKey={queryKey} />
              ) : null}
            </>
          )}
        </div>
      </div>

      {/*
        One ChatPanel, mounted for the life of the page and hidden rather than
        unmounted, so opening and closing it does not destroy the thread.

        `chatEnabled` says whether the panel is available on this page at all.
        It does not say a model is attached — the panel reads that from
        /api/chat/config and labels its own replies as a stub.
      */}
      {data.chatEnabled ? (
        <div
          hidden={!chatOpen}
          className={clsx(
            'fixed z-40',
            wide
              ? 'top-20 right-4 bottom-4 w-[26rem]'
              : 'inset-x-0 bottom-0 h-[80vh] px-3 pb-3',
          )}
        >
          <div className="flex justify-end pb-2">
            <button
              type="button"
              onClick={() => setChatOpen(false)}
              className="inline-flex items-center gap-1.5 rounded-full border border-line-strong bg-surface px-3 py-1.5 text-xs font-medium text-ink shadow-pop"
            >
              <X size={14} aria-hidden />
              Close the assistant
            </button>
          </div>
          <ChatPanel
            scopeType="capstone"
            scopeId={data.id}
            scopeLabel={`the “${data.title}” brief`}
            className="h-[calc(100%-2.25rem)] shadow-pop"
          />
        </div>
      ) : null}
    </PageContainer>
  );
}

// -------------------------------------------------------------------- hero ---

function Hero({
  capstone,
  milestonesDone,
}: {
  capstone: CapstoneForLearner;
  milestonesDone: number;
}) {
  return (
    <header className="rounded-card border border-project/25 bg-project-wash p-6 sm:p-8">
      <Link
        to={`/courses/${capstone.courseSlug}`}
        className="text-sm text-ink-muted hover:text-ink inline-flex items-center gap-1.5"
      >
        <ArrowLeft size={14} aria-hidden />
        {capstone.courseTitle}
      </Link>

      <div className="flex flex-wrap items-start gap-6 mt-4">
        <span
          aria-hidden
          className="grid place-items-center h-12 w-12 shrink-0 rounded-card bg-surface border border-project/30 text-project"
        >
          <Trophy size={22} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="eyebrow text-project">Capstone project</p>
          <h1 className="text-3xl font-semibold tracking-tight text-ink mt-1.5">
            {capstone.title}
          </h1>
          {capstone.subtitle ? (
            <p className="text-base text-ink-muted mt-2 max-w-prose leading-relaxed">
              {capstone.subtitle}
            </p>
          ) : null}

          <div className="flex flex-wrap items-center gap-2 mt-4">
            <Badge tone="project">Ungraded — build it for yourself</Badge>
            {capstone.effortEstimate ? <Badge>{capstone.effortEstimate}</Badge> : null}
            {capstone.status !== 'published' ? <Badge tone="warn">Draft</Badge> : null}
          </div>

          {capstone.prerequisites ? (
            <p className="text-sm text-ink-muted mt-4 max-w-prose leading-relaxed">
              <span className="font-medium text-ink">Before you start:</span>{' '}
              {capstone.prerequisites}
            </p>
          ) : null}

          {capstone.milestones.length > 0 ? (
            <div className="mt-5 max-w-sm">
              <div className="flex items-center justify-between gap-3 mb-1.5">
                <span className="text-xs font-medium text-ink-muted">Milestones</span>
                <DotProgress done={milestonesDone} total={capstone.milestones.length} />
              </div>
              <ProgressBar
                tone="brand"
                value={(milestonesDone / capstone.milestones.length) * 100}
                label="Milestones you have ticked off"
              />
            </div>
          ) : null}

          <p className="text-xs text-ink-subtle mt-4 max-w-prose leading-relaxed">
            Nothing here is submitted, scored or reviewed. The ticks are yours, the deadline is
            yours, and the only person the finished thing has to convince is you.
          </p>
        </div>
      </div>
    </header>
  );
}

// ----------------------------------------------------------------- sidebar ---

type TocSection = { id: string; label: string };

function buildSections(capstone: CapstoneForLearner | undefined): TocSection[] {
  if (!capstone) return [];
  const list: TocSection[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'problem', label: 'The problem' },
  ];
  if (capstone.locked) return list;
  if (capstone.specSections.length) list.push({ id: 'specification', label: 'What it has to do' });
  if (capstone.milestones.length) list.push({ id: 'milestones', label: 'Milestones' });
  if (capstone.hints.length) list.push({ id: 'hints', label: 'Hints' });
  if (capstone.approaches.length) list.push({ id: 'approaches', label: 'Approaches' });
  if (capstone.resources.length) list.push({ id: 'resources', label: 'Starter resources' });
  if (capstone.selfChecks.length) list.push({ id: 'self-check', label: 'Self-check' });
  if (capstone.hasReferenceSolution) list.push({ id: 'solution', label: 'Reference solution' });
  return list;
}

function Sidebar({
  sections,
  activeId,
  wide,
  hasStarters,
  canAskAi,
  onAskAi,
}: {
  sections: TocSection[];
  activeId: string | null;
  wide: boolean;
  hasStarters: boolean;
  /** Whether the assistant panel is available on this page at all. */
  canAskAi: boolean;
  onAskAi: () => void;
}) {
  const nav = (
    <nav aria-label="Sections of this brief">
      <ol className="flex flex-col gap-0.5">
        {sections.map((section) => (
          <li key={section.id}>
            <a
              href={`#${section.id}`}
              aria-current={activeId === section.id ? 'true' : undefined}
              className={clsx(
                'block border-l-2 pl-2.5 pr-2 py-1.5 text-sm rounded-r-control',
                'transition-colors duration-150 ease-out',
                activeId === section.id
                  ? 'border-brand bg-surface-2 text-ink font-semibold'
                  : 'border-line text-ink-muted hover:text-ink hover:bg-surface-2 hover:border-line-strong',
              )}
            >
              {section.label}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );

  // Nothing to act on means no divider under the contents list.
  const actions =
    canAskAi || hasStarters ? (
      <div className="flex flex-col gap-2 pt-3 mt-3 border-t border-line">
        {canAskAi ? (
          <Button
            variant="secondary"
            size="sm"
            icon={<MessageSquare size={15} aria-hidden />}
            onClick={onAskAi}
          >
            Ask AI about this brief
          </Button>
        ) : null}
        {hasStarters ? (
          <a
            href="#resources"
            className="inline-flex items-center justify-center gap-2 rounded-control border border-line-strong bg-surface px-3 py-1.5 text-sm font-medium text-ink hover:bg-surface-2 transition"
          >
            <Package size={15} aria-hidden />
            Starter resources
          </a>
        ) : null}
      </div>
    ) : null;

  if (!wide) {
    return (
      <div className="lg:hidden">
        <Disclosure summary="Jump to a section">
          {nav}
          {actions}
        </Disclosure>
      </div>
    );
  }

  return (
    <aside className="sticky top-20 self-start">
      <p className="eyebrow mb-2 px-2.5">
        On this page
      </p>
      {nav}
      {actions}
    </aside>
  );
}

// ---------------------------------------------------------------- sections ---

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} aria-labelledby={`${id}-heading`}>
      <h2 id={`${id}-heading`} className="text-xl font-semibold tracking-tight text-ink mb-3">
        {title}
      </h2>
      {children}
    </section>
  );
}

function Milestones({
  capstone,
  queryKey,
  done,
}: {
  capstone: CapstoneForLearner;
  queryKey: readonly unknown[];
  done: number;
}) {
  const toggle = useChecklistToggle<CapstoneForLearner>(queryKey, (previous, itemId, isDone) => ({
    ...previous,
    milestones: previous.milestones.map((milestone) =>
      milestone.id === itemId ? { ...milestone, done: isDone } : milestone,
    ),
  }));

  return (
    <Section id="milestones" title="Milestones">
      <p className="text-sm text-ink-muted mb-4 max-w-prose leading-relaxed">
        A workable order to build this in. Tick them off as you go — {done} of{' '}
        {capstone.milestones.length} so far. The ticks are stored against your account and nobody
        else ever sees them.
      </p>
      <Card className="divide-y divide-line">
        {[...capstone.milestones]
          .sort((a, b) => a.orderIndex - b.orderIndex)
          .map((milestone, index) => (
            <div key={milestone.id} className="p-4 flex items-start gap-3">
              <input
                id={`milestone-${milestone.id}`}
                type="checkbox"
                checked={milestone.done}
                onChange={(e) =>
                  toggle.mutate({
                    itemType: 'capstone_milestone',
                    itemId: milestone.id,
                    done: e.target.checked,
                  })
                }
                className="mt-1 h-4 w-4 shrink-0"
              />
              <div className="min-w-0 flex-1">
                <label
                  htmlFor={`milestone-${milestone.id}`}
                  className={clsx(
                    'font-medium cursor-pointer flex flex-wrap items-baseline gap-2',
                    milestone.done ? 'text-ink-muted line-through' : 'text-ink',
                  )}
                >
                  <span className="tabular-nums text-ink-subtle text-sm no-underline">
                    {index + 1}.
                  </span>
                  {milestone.title}
                  {milestone.effortEstimate ? (
                    <Badge>{milestone.effortEstimate}</Badge>
                  ) : null}
                </label>
                {milestone.bodyMd ? (
                  <div className="mt-1.5">
                    <Markdown compact>{milestone.bodyMd}</Markdown>
                  </div>
                ) : null}
              </div>
            </div>
          ))}
      </Card>
    </Section>
  );
}

function HintBlock({ hint, queryKey }: { hint: CapstoneHint; queryKey: readonly unknown[] }) {
  const reveal = useReveal(queryKey);

  if (hint.revealed && hint.bodyMd) {
    return (
      <Disclosure
        summary={
          <span className="flex items-center gap-2">
            <Lightbulb size={15} className="text-warn" aria-hidden />
            {hint.title}
          </span>
        }
        defaultOpen
      >
        <Markdown compact>{hint.bodyMd}</Markdown>
      </Disclosure>
    );
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-control border border-line bg-surface px-4 py-3">
      <span className="flex items-center gap-2 text-sm font-medium text-ink min-w-0">
        <Lightbulb size={15} className="text-ink-subtle shrink-0" aria-hidden />
        <span className="truncate">{hint.title}</span>
      </span>
      <Button
        size="sm"
        variant="secondary"
        loading={reveal.isPending}
        onClick={() => reveal.mutate({ targetType: 'capstone_hint', targetId: hint.id })}
      >
        Reveal hint
        <span className="sr-only"> “{hint.title}”</span>
      </Button>
    </div>
  );
}

function ApproachBlock({
  approach,
  queryKey,
}: {
  approach: CapstoneApproach;
  queryKey: readonly unknown[];
}) {
  const reveal = useReveal(queryKey);

  if (approach.revealed) {
    return (
      <Disclosure summary={approach.title} defaultOpen={!approach.revealGated}>
        <Markdown compact>{approach.bodyMd}</Markdown>
        {approach.tradeoffsMd ? (
          <div className="mt-3 border-t border-line pt-3">
            <p className="eyebrow mb-1">
              Trade-offs
            </p>
            <Markdown compact>{approach.tradeoffsMd}</Markdown>
          </div>
        ) : null}
      </Disclosure>
    );
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-control border border-line bg-surface px-4 py-3">
      <span className="text-sm font-medium text-ink min-w-0 truncate">{approach.title}</span>
      <Button
        size="sm"
        variant="secondary"
        icon={<Eye size={14} aria-hidden />}
        loading={reveal.isPending}
        onClick={() => reveal.mutate({ targetType: 'capstone_approach', targetId: approach.id })}
      >
        Show this approach
        <span className="sr-only"> — “{approach.title}”</span>
      </Button>
    </div>
  );
}

function SelfChecks({
  capstone,
  queryKey,
}: {
  capstone: CapstoneForLearner;
  queryKey: readonly unknown[];
}) {
  const toggle = useChecklistToggle<CapstoneForLearner>(queryKey, (previous, itemId, isDone) => ({
    ...previous,
    selfChecks: previous.selfChecks.map((check) =>
      check.id === itemId ? { ...check, done: isDone } : check,
    ),
  }));

  const done = capstone.selfChecks.filter((check) => check.done).length;

  return (
    <Section id="self-check" title="Check it yourself">
      <p className="text-sm text-ink-muted mb-4 max-w-prose leading-relaxed">
        What “finished” looks like, in the author’s words. Nobody marks this — {done} of{' '}
        {capstone.selfChecks.length} ticked.
      </p>
      <Card className="divide-y divide-line">
        {[...capstone.selfChecks]
          .sort((a, b) => a.orderIndex - b.orderIndex)
          .map((check) => (
            <div key={check.id} className="p-3.5 flex items-start gap-3">
              <input
                id={`self-check-${check.id}`}
                type="checkbox"
                checked={check.done}
                onChange={(e) =>
                  toggle.mutate({
                    itemType: 'capstone_self_check',
                    itemId: check.id,
                    done: e.target.checked,
                  })
                }
                className="mt-0.5 h-4 w-4 shrink-0"
              />
              <label
                htmlFor={`self-check-${check.id}`}
                className={clsx(
                  'text-sm cursor-pointer inline-flex items-start gap-2',
                  check.done ? 'text-ink-muted' : 'text-ink',
                )}
              >
                <span aria-hidden className="mt-0.5 shrink-0">
                  {check.done ? (
                    <CheckCircle2 size={14} className="text-ok" />
                  ) : (
                    <Circle size={14} className="text-ink-subtle" />
                  )}
                </span>
                {check.label}
              </label>
            </div>
          ))}
      </Card>
    </Section>
  );
}

function ReferenceSolution({
  capstone,
  queryKey,
}: {
  capstone: CapstoneForLearner;
  queryKey: readonly unknown[];
}) {
  const reveal = useReveal(queryKey);
  const [open, setOpen] = useState(false);

  return (
    <Section id="solution" title="Reference solution">
      {capstone.solutionRevealed && capstone.referenceSolutionMd ? (
        <Card className="p-5">
          <Markdown>{capstone.referenceSolutionMd}</Markdown>
        </Card>
      ) : (
        <Card className="p-5 border-warn/40 bg-warn-wash/40">
          <div className="flex items-start gap-3">
            <span aria-hidden className="mt-0.5 text-warn shrink-0">
              <Lock size={18} />
            </span>
            <div className="min-w-0">
              <p className="text-sm text-ink leading-relaxed max-w-prose">
                One complete solution, written by the author. It is worth far more after you have
                built something of your own — even something half-working — than it is now. Open it
                and it stays open.
              </p>
              <Button
                className="mt-4"
                variant="secondary"
                icon={<Eye size={15} aria-hidden />}
                onClick={() => setOpen(true)}
              >
                Reveal the reference solution
              </Button>
            </div>
          </div>
        </Card>
      )}

      <ConfirmDialog
        open={open}
        onClose={() => setOpen(false)}
        title="Reveal the reference solution?"
        description="This cannot be undone. The solution stays visible on this page from now on, on every device you sign in from."
        confirmLabel="Reveal it"
        requirePhrase="reveal"
        loading={reveal.isPending}
        details={
          <Alert tone="warn" title="Have you built something first?">
            The capstone is the part of this course that teaches by being difficult. Reading the
            answer before you have wrestled with the problem is the one way to get nothing out of
            it — and nothing here is graded, so there is no reason to rush to it.
          </Alert>
        }
        onConfirm={async () => {
          await reveal.mutateAsync({
            targetType: 'capstone_solution',
            targetId: capstone.id,
            confirm: true,
          });
          setOpen(false);
        }}
      />
    </Section>
  );
}
